import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import apiService from '../../services/api';
import {
  Box,
  Button,
  Container,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import './Onboarding.css';

const PHASE_1_REQUIRED_FIELDS = [
  'sex_at_birth',
  'date_of_birth',
  'height_ft',
  'height_in',
  'weight_value',
  'weight_unit',
  'primary_objectives',
];

function isPhase1Complete(responses) {
  if (!responses || typeof responses !== 'object') return false;
  return PHASE_1_REQUIRED_FIELDS.every((field) => {
    const val = responses[field];
    if (val === undefined || val === null || val === '') return false;
    if (Array.isArray(val) && val.length === 0) return false;
    return true;
  });
}

function Onboarding() {
  const { user, loading: authLoading, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const didAutoComplete = useRef(false);

  useEffect(() => {
    if (!user || didAutoComplete.current) return;

    async function checkAndAutoComplete() {
      setChecking(true);
      try {
        const draftRes = await apiService.questionnaires.getDraft('intake');
        const draft = draftRes.data?.draft ?? null;
        const complete = draft && isPhase1Complete(draft.responses);

        if (complete) {
          didAutoComplete.current = true;
          await apiService.users.completeOnboarding();
          await refreshMe();
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch (e) {
        console.error('Onboarding check failed:', e);
      } finally {
        setChecking(false);
      }
    }

    checkAndAutoComplete();
  }, [user, refreshMe, navigate]);

  if (authLoading) {
    return (
      <Box className="onboarding-page">
        <div className="onboarding-loading-wrap">
          <Container size="xs">
            <Stack align="center" gap="md"><Loader size="lg" color="pink" /><Text c="dimmed">Loading...</Text></Stack>
          </Container>
        </div>
      </Box>
    );
  }

  if (user?.onboarding_completed) {
    return <Navigate to="/dashboard" replace />;
  }

  if (checking) {
    return (
      <Box className="onboarding-page">
        <div className="onboarding-loading-wrap">
          <Container size="xs">
            <Stack align="center" gap="md"><Loader size="md" color="pink" /><Text c="dimmed" size="sm">Checking your progress...</Text></Stack>
          </Container>
        </div>
      </Box>
    );
  }

  return (
    <Box className="onboarding-page">
      <div className="onboarding-container">
        <div className="onboarding-card">
          <Stack align="center" gap="md">
            {/* Editable copy – change title and text below */}
            <Title order={1} className="onboarding-title">
              Welcome to Open Health
            </Title>
            <Text className="onboarding-text" maw={480}>
              We'll help you store and analyze your health data in one place.
            </Text>
            <Text className="onboarding-text" maw={480} fw={700}>
              But first we need a bit of information about you.
            </Text>
            <Text className="onboarding-text" maw={480}>
              It only takes a minute.
            </Text>
            <div className="onboarding-button-wrap">
              <Button
                size="lg"
                color="pink"
                rightSection={<IconChevronRight size={18} />}
                onClick={() => navigate('/questionnaires?from=onboarding')}
              >
                Get Started
              </Button>
            </div>
          </Stack>
        </div>
      </div>
    </Box>
  );
}

export default Onboarding;
